import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import PageMeta from '@/components/base/PageMeta';
import { categories, Category } from '@/mocks/categories';
import { getArticlesByCategory, Article } from '@/mocks/articles';

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [currentCategory, setCurrentCategory] = useState<Category | null>(null);
  const [displayedArticles, setDisplayedArticles] = useState<Article[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryId]);

  useEffect(() => {
    if (categoryId) {
      const cat = categories.find((c) => c.slug === categoryId) || categories[0];
      setCurrentCategory(cat);
      setDisplayedArticles(getArticlesByCategory(cat.id));
    }
  }, [categoryId]);

  if (!currentCategory) {
    return null;
  }

  const categoryTitle = `久久久久${currentCategory.name} - 持久稳定体验`;
  const pageUrl = `https://jiujiujiujiu.com/category/${currentCategory.slug}`;

  return (
    <PageMeta
      title={categoryTitle}
      description={currentCategory.description}
      keywords={`久久久久,久久久久${currentCategory.name},持久体验,稳定性能`}
      canonicalUrl={pageUrl}
    >
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jiujiujiujiu.com' },
          { '@type': 'ListItem', position: 2, name: currentCategory.name, item: pageUrl },
        ],
      }} />

      <SchemaOrg type="WebPage" data={{
        name: categoryTitle,
        url: pageUrl,
        description: currentCategory.description,
        isPartOf: { '@type': 'WebSite', name: '久久久久', url: 'https://jiujiujiujiu.com' },
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <section className="pt-24 pb-10 md:pt-28 md:pb-14 bg-background-100">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentCategory.name },
                ]}
              />
              <div className="mt-6">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                  久久久久{currentCategory.name}
                </h1>
                <p className="text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
                  {currentCategory.description}
                </p>
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 bg-background-50">
            <div className="max-w-6xl mx-auto px-4 md:px-6">
              <div className="flex gap-3 md:gap-4 overflow-x-auto pb-2 mb-8 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      currentCategory.id === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 border border-background-200 hover:border-primary-300 hover:text-primary-500'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {displayedArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {displayedArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-100 rounded-lg border border-background-200/70 hover:border-primary-300 transition-all duration-300 overflow-hidden cursor-pointer"
                    >
                      <div className="w-full h-48 overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-5">
                        <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">
                          {article.summary}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <span>{article.publishDate}</span>
                          <span>{article.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </PageMeta>
  );
}